import { MouseEvent, ReactNode, RefObject, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { Spinner } from '@nx-frontend/shared/ui/spinner';
import { AiMentionType } from '@nx-frontend/shared/types';
import { ChatbotContext, MentionsInputContext } from './chatbot-context';
import { $aiMentions, fetchMentionsEvent } from './store';

function LazyLoadingWrapper({ children, listRef }: { children: ReactNode; listRef: RefObject<HTMLDivElement> }) {
  const aiMentions = useUnit($aiMentions);
  const lazyLoadingRef = useRef<HTMLSpanElement>(null);
  const { sessionId, apiHost, workspaceId, portalSettings, portalId } = useContext(ChatbotContext);
  const fetchMentions = useUnit(fetchMentionsEvent);

  useEffect(() => {
    if (listRef.current && lazyLoadingRef.current) {
      const llObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (entry.isIntersecting && aiMentions.total) {
            if (typeof aiMentions.total === 'number' && aiMentions.items.length < aiMentions.total) {
              fetchMentions({
                type: aiMentions.showAll,
                sessionId,
                apiHost,
                workspaceId,
                query: aiMentions.query ?? '',
                limit: 10,
                portalId: portalSettings?.id,
                offset: aiMentions.items.length,
                onlyPortal: Boolean(portalId),
              });
            }
          }
        },
        {
          root: listRef.current,
          threshold: 1.0,
        }
      );
      llObserver.observe(lazyLoadingRef.current);
      return () => {
        llObserver.disconnect();
      };
    }
  }, [
    aiMentions.items.length,
    aiMentions.query,
    aiMentions.showAll,
    aiMentions.total,
    apiHost,
    fetchMentions,
    listRef,
    portalSettings?.id,
    sessionId,
    workspaceId,
    portalId,
  ]);

  return (
    <>
      {children}
      <span ref={lazyLoadingRef} className="flex w-full justify-center min-h-[1px]">
        {aiMentions.isLazyFetching ? <Spinner width={12} height={12} /> : null}
      </span>
    </>
  );
}

function MentionsScrollWrapper({
  children,
  useLazyLoading = false,
}: {
  children: ReactNode;
  useLazyLoading?: boolean;
}) {
  const [isKeyNavigated, setIsKeyNavigated] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const observer = useRef<IntersectionObserver | null>(null);
  const { textInputRef } = useContext(MentionsInputContext);

  useEffect(() => {
    if (textInputRef && textInputRef.current) {
      const onDocumentKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
          setIsKeyNavigated(true);
        }
      };
      const node = textInputRef.current;
      node.addEventListener('keydown', onDocumentKeyDown);
      return () => {
        node.removeEventListener('keydown', onDocumentKeyDown);
      };
    }
  }, [textInputRef]);

  useEffect(() => {
    if (listRef.current) {
      observer.current = new IntersectionObserver(
        (entries) => {
          setTimeout(() => {
            if (isKeyNavigated) {
              const [entry] = entries;
              if (!entry.isIntersecting) {
                const item = entry.target as HTMLLIElement;
                const list = listRef.current as HTMLDivElement;

                const itemTop = item.offsetTop;
                const itemBottom = itemTop + item.offsetHeight;
                const listScrollTop = list.scrollTop;
                const listHeight = list.offsetHeight;

                if (itemTop < listScrollTop) {
                  // Прокручиваем вверх, добавляя отступ
                  list.scrollTo({
                    top: itemTop - 20,
                  });
                } else if (itemBottom > listScrollTop + listHeight) {
                  // Прокручиваем вниз, добавляя отступ
                  list.scrollTo({
                    top: itemBottom - listHeight + 20,
                  });
                }
              }
              setIsKeyNavigated(false);
            }
          }, 0);
        },
        {
          root: listRef.current,
          threshold: 1.0,
        }
      );

      const focusedElement = listRef.current.querySelector('.chatbot-input__suggestions__item--focused');
      if (focusedElement) {
        observer.current?.observe(focusedElement);
      }

      return () => {
        observer.current?.disconnect();
      };
    }
  }, [children, isKeyNavigated]);

  return (
    <div
      className="chatbot-mentions-container w-64 max-h-[364px] overflow-y-auto py-[7px] px-[3px] rounded-lg border border-neutral-200 text-[13px] bg-white"
      ref={listRef}
    >
      {useLazyLoading ? <LazyLoadingWrapper listRef={listRef}>{children}</LazyLoadingWrapper> : children}
    </div>
  );
}

function MentionsGroup({ title, mentions, type }: { title: string; mentions: JSX.Element[]; type: AiMentionType }) {
  const { sessionId, apiHost, workspaceId, portalSettings, portalId } = useContext(ChatbotContext);
  const [aiMentions, fetchMentions] = useUnit([$aiMentions, fetchMentionsEvent]);
  const t = useTranslations('features');

  const onShowAllMouseDown = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      fetchMentions({
        type,
        sessionId,
        apiHost,
        workspaceId,
        query: aiMentions.query ?? '',
        limit: 10,
        portalId: portalSettings?.id,
        onlyPortal: Boolean(portalId),
      });
    },
    [aiMentions.query, apiHost, fetchMentions, portalSettings, sessionId, type, workspaceId, portalId]
  );

  const total = aiMentions.total;

  return (
    <div className="py-2 border-t border-neutral-200">
      <div className="px-4 py-1">{title}</div>
      <ul className="chatbot-input__suggestions__list">{mentions.map((mention: JSX.Element) => mention)}</ul>
      {!aiMentions.query &&
      typeof total !== 'number' &&
      typeof total[type] === 'number' &&
      mentions.length < (total[type] as number) ? (
        <button
          className="px-4 py-1 text-neutral-400 hover:text-neutral-800 w-full text-left"
          onMouseDown={onShowAllMouseDown}
        >
          {t('ai-helper.show_all')}
        </button>
      ) : null}
    </div>
  );
}

function MentionsList({ mentions }: { mentions: JSX.Element[] }) {
  const { sessionId, apiHost, workspaceId, portalSettings, portalId } = useContext(ChatbotContext);
  const [aiMentions, fetchMentions] = useUnit([$aiMentions, fetchMentionsEvent]);
  const t = useTranslations('features');

  const onBackMouseDown = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      fetchMentions({
        type: undefined,
        sessionId,
        apiHost,
        workspaceId,
        query: aiMentions.query ?? '',
        limit: aiMentions.query !== '' ? 10 : 3,
        portalId: portalSettings?.id,
        onlyPortal: Boolean(portalId),
      });
    },
    [aiMentions.query, apiHost, fetchMentions, portalSettings, sessionId, workspaceId, portalId]
  );

  return (
    <>
      <div className="pb-2 mb-2 border-b border-neutral-200">
        <div
          className="px-4 py-1 flex items-center gap-2 hover:bg-neutral-100 cursor-pointer"
          onMouseDown={onBackMouseDown}
        >
          <div className="flex items-center justify-center w-6 h-6 text-xs rounded-full overflow-hidden bg-neutral-100 text-neutral-500 flex-none">
            <Icon icon={IconsMap.ARROW_LEFT_BACK} className="w-4" />
          </div>
          {t('ai-helper.back')}
        </div>
      </div>
      <ul className="chatbot-input__suggestions__list">{mentions.map((mention) => mention)}</ul>
    </>
  );
}

function ChatbotMentionsMutable({ children }: { children: ReactNode }) {
  const aiMentions = useUnit($aiMentions);
  const t = useTranslations('features');
  const groupMentions = useCallback((mentions: JSX.Element[]) => {
    return mentions.reduce(
      (
        lists: {
          [group: string]: JSX.Element[];
        },
        mention: JSX.Element
      ) => {
        const { suggestion } = mention.props;
        if (!(suggestion.type in lists)) {
          lists[suggestion.type as string] = [];
        }
        lists[suggestion.type].push(mention);
        return lists;
      },
      {}
    );
  }, []);

  // Group mentions by id to avoid duplicates then convert it to array
  const mentions = useMemo(() => {
    const { children: mentions } = (children as JSX.Element).props;
    const entities: Record<string, JSX.Element> = {};
    if (mentions) {
      mentions.forEach((mention: JSX.Element) => {
        const { suggestion } = mention.props;
        if (suggestion.id) {
          entities[suggestion.id] = mention;
        }
      });
    }
    return Object.values(entities);
  }, [children]);

  const groups = !aiMentions.showAll && mentions.length ? groupMentions(mentions) : false;

  return children && !aiMentions.isFetching ? (
    groups ? (
      <MentionsScrollWrapper>
        <>
          {groups.workspace || groups[AiMentionType.FOLDER] || groups[AiMentionType.NOTE] ? (
            <>
              <div className="chatbot-mentions-list-section px-4 pt-2.5 pb-1.5 uppercase text-neutral-500 bg-neutral-100">
                {t('ai-helper.internal_space')}
              </div>
              {groups.workspace ? (
                <div className="py-2">
                  <ul className="chatbot-input__suggestions__list">
                    {groups.workspace.map((mention: JSX.Element) => mention)}
                  </ul>
                </div>
              ) : null}
              {groups[AiMentionType.FOLDER] ? (
                <MentionsGroup
                  title={t('ai-helper.folders')}
                  mentions={groups[AiMentionType.FOLDER]}
                  type={AiMentionType.FOLDER}
                />
              ) : null}
              {groups[AiMentionType.NOTE] ? (
                <MentionsGroup
                  title={t('ai-helper.pages')}
                  mentions={groups[AiMentionType.NOTE]}
                  type={AiMentionType.NOTE}
                />
              ) : null}
            </>
          ) : null}
          {groups.portal || groups[AiMentionType.PORTAL_SECTION] || groups[AiMentionType.PORTAL_NOTE] ? (
            <>
              <div className="px-4 pt-2.5 pb-1.5 uppercase text-neutral-500 bg-neutral-100">
                {t('ai-helper.client_portal')}
              </div>
              {groups.portal ? (
                <div className="py-2">
                  <ul className="chatbot-input__suggestions__list">
                    {groups.portal.map((mention: JSX.Element) => mention)}
                  </ul>
                </div>
              ) : null}
              {groups[AiMentionType.PORTAL_SECTION] ? (
                <MentionsGroup
                  title={t('ai-helper.portal_folders')}
                  mentions={groups[AiMentionType.PORTAL_SECTION]}
                  type={AiMentionType.PORTAL_SECTION}
                />
              ) : null}
              {groups[AiMentionType.PORTAL_NOTE] ? (
                <MentionsGroup
                  title={t('ai-helper.portal_pages')}
                  mentions={groups[AiMentionType.PORTAL_NOTE]}
                  type={AiMentionType.PORTAL_NOTE}
                />
              ) : null}
            </>
          ) : null}
        </>
      </MentionsScrollWrapper>
    ) : mentions.length ? (
      <MentionsScrollWrapper useLazyLoading={true}>
        <MentionsList mentions={mentions} />
      </MentionsScrollWrapper>
    ) : (
      <div className="w-full py-2 flex justify-center">
        <Spinner width={32} height={32} />
      </div>
    )
  ) : (
    <div className="chatbot-mentions-container w-64 py-[7px] px-[3px] rounded-lg border border-neutral-200 text-[13px] bg-white">
      <div className="w-full py-2 flex justify-center">
        <Spinner width={32} height={32} />
      </div>
    </div>
  );
}

export function ChatbotMentions(children: ReactNode) {
  return <ChatbotMentionsMutable>{children}</ChatbotMentionsMutable>;
}
